import { For, Show, createSignal, onCleanup, type Accessor, type JSX } from 'solid-js';
import type { OllamaSnapshot } from '../state/metrics';
import { spilloverPct, keepAliveCountdown } from './ollama-derive';
import { HudPanel } from '../ui/hud-panel';
import { InfoTip } from '../ui/info-tip';

/** Plain-language tooltip copy, mirrored verbatim from the mockup's `data-tip` text. */
const TIPS = {
  unloadsIn:
    'Ollama keeps a model in memory for a few idle minutes, then frees it so other apps can use the card. This counts down only while nothing is using the model — a new request resets it. Once it unloads, your next message waits a few seconds for it to reload.',
  runningOnCpu:
    "How much of the model didn't fit on the graphics card and is running on the slower CPU instead. 0% = all on the GPU (fast); the higher it is, the slower the replies.",
} as const;

const STATE_CHIP: Record<'good' | 'warn' | 'unknown', string> = {
  good: 'text-good bg-good-soft',
  warn: 'text-warn bg-warn-soft',
  unknown: 'text-faint bg-surface-2',
};

/** A HUD key/value line: small-caps label left, value content right. Matches the
 * llama.cpp card's Row so the two runner cards read as a pair. */
function Row(props: { label: JSX.Element; children: JSX.Element }) {
  return (
    <div class="flex items-center justify-between gap-2.5 border-t border-line/40 py-1.5 first:border-t-0 text-[0.83rem]">
      <span class="hud-mono inline-flex items-center gap-1 text-[0.625rem] uppercase tracking-[0.18em] text-faint">
        {props.label}
      </span>
      {props.children}
    </div>
  );
}

/** Ollama runner card. Restyled to the same HUD idiom as the llama.cpp card so the two
 * runner cards read as a pair. */
export function OllamaCard(props: { data: Accessor<OllamaSnapshot | null> }) {
  const [nowMs, setNowMs] = createSignal(Date.now());
  const timer = setInterval(() => setNowMs(Date.now()), 1000);
  onCleanup(() => clearInterval(timer));

  const snap = () => props.data();
  // A `null` snapshot means no ollama frame has arrived yet — the agent may not even
  // poll Ollama, or just hasn't reported in. Distinct from a frame that positively
  // says `reachable: false`: only the latter is a confirmed "isn't responding". Mirrors
  // the `if (ollama && !ollama.reachable)` shape in health-derive.ts.
  const hasFrame = () => snap() != null;
  const reachable = () => snap()?.reachable === true;
  const status = (): 'good' | 'warn' | 'unknown' => {
    if (!hasFrame()) return 'unknown';
    return reachable() ? 'good' : 'warn';
  };
  const loaded = () => snap()?.loaded[0] ?? null;
  const models = () => snap()?.models ?? [];

  return (
    <HudPanel title="Ollama" sub={snap()?.version ? `v${snap()!.version}` : undefined}>
      <div class="mb-3 flex items-center gap-2">
        <span
          class={`inline-flex h-[13px] w-[13px] flex-none items-center justify-center rounded-full ${
            STATE_CHIP[status()]
          }`}
        >
          <span class="h-1.5 w-1.5 rounded-full bg-current" />
        </span>
      </div>

      <Show
        when={hasFrame()}
        fallback={<p class="text-[0.83rem] text-muted">waiting for Ollama telemetry…</p>}
      >
        <Show
          when={reachable()}
          fallback={
            <p class="text-[0.83rem] text-muted">
              Ollama isn't responding — models can't be loaded or unloaded until it's back. Check that the Ollama
              service is running.
            </p>
          }
        >
          <div class="flex flex-col">
            <Show
              when={loaded()}
              fallback={
                <Row label="Loaded model">
                  <span class="font-mono tabular-nums text-ink">none</span>
                </Row>
              }
            >
              {(m) => {
                const spill = () => spilloverPct(m().vramMb, m().sizeMb);
                const countdown = () => keepAliveCountdown(m().expiresAt, nowMs());
                return (
                  <>
                    <Row label="Loaded model">
                      <span class="font-mono tabular-nums text-ink">{m().name}</span>
                    </Row>
                    <Row
                      label={
                        <>
                          Unloads in <InfoTip text={TIPS.unloadsIn} />
                        </>
                      }
                    >
                      <span class="font-mono font-bold tabular-nums text-accent">{countdown() ?? '—'}</span>
                    </Row>
                    <Row
                      label={
                        <>
                          Running on CPU <InfoTip text={TIPS.runningOnCpu} />
                        </>
                      }
                    >
                      <Show when={spill() > 0} fallback={<span class="font-mono tabular-nums text-good">0%</span>}>
                        <span class="inline-flex items-center gap-1 rounded-full bg-crit-soft px-2 py-0.5 text-[0.71rem] font-semibold text-crit">
                          <span class="h-1.5 w-1.5 rounded-full bg-current" />
                          {spill()}% in RAM
                        </span>
                      </Show>
                    </Row>
                  </>
                );
              }}
            </Show>
          </div>

          <details class="mt-2 border-t border-line pt-1">
            <summary class="hud-mono cursor-pointer list-none py-2 text-[0.6563rem] uppercase tracking-[0.14em] text-holo opacity-85">
              {models().length} models installed
            </summary>
            <For each={models()}>
              {(m) => (
                <div class="flex items-center justify-between gap-2.5 border-b border-line/40 py-2 text-xs last:border-b-0">
                  <span class="font-semibold">{m.name}</span>
                  {/* Ollama's catalog `size` is `#[serde(default)]` on the agent, so an entry
                      Ollama didn't report a size for arrives as 0, not absent — a non-positive
                      or non-finite value means "unknown", not "0 GB". */}
                  <Show when={Number.isFinite(m.sizeMb) && m.sizeMb > 0}>
                    <span class="tabular-nums text-faint">{(m.sizeMb / 1024).toFixed(1)} GB</span>
                  </Show>
                </div>
              )}
            </For>
          </details>
        </Show>
      </Show>

      <p class="mt-2 border-t border-line/40 py-1.5 text-[0.8rem] text-muted">
        Ollama frees an idle model from memory after a few minutes so other apps can use the card. Harmless — but
        the{' '}
        <b class="font-semibold text-ink">
          first message after that reloads the model, so it's slow for a few seconds
        </b>
        . Send anything to keep it warm if you'll need it soon.
      </p>
    </HudPanel>
  );
}
